import React, { useState, useEffect, useRef, useContext } from "react";
import { ListPanel } from "../components/common/ListPanel";
import { Redirect, Switch, Route, useLocation } from "react-router-dom";
import { NavBarMain } from "../components/NavBarMain";
import { DashboardHeader } from "../components/DashboardHeader";
import { NavBarTodos } from "../components/NavBarTodos";
import { NavBarRegReports } from "../components/NavBarRegReports";
import { Links } from "../components/Links";
import {
	getUserSettings,
	loadUserSettings,
	loadUserPid,
	getUserPid,
	isDemoUser,
	isRegistrar,
	getDiseaseGroup,
} from "../services/userService";
import { HelpPopup } from "../components/HelpPopup";
import { LoadingSpinner } from "../components/common/LoadingSpinner";
import { NavBarYourSummary } from "../components/summary/NavBarYourSummary";
import { YourSummary } from "./yoursummary/YourSummary.jsx";
import { NavBarYourCosts } from "../components/your-costs/NavBarYourCosts";
import { YourCosts } from "../components/your-costs/YourCosts";
import { getUsefulLinks } from "../services/dashApiService";
import { TodoList } from "../components/dashboard/TodoList";
import { TransitionSlideUp } from "../components/transitions/TransitionSlideUp";
import { TransitionConfig } from "../configs/transitionConfig";
import { CostPopup } from "../components/your-costs/CostPopup";
import { PathsToHelpCodesMap } from "../PathsToHelpCodesMap";
import {
	isLandscape,
	subscribeToLandscapeChange,
	unsubscribeFromLandscapeChange,
	isXamarin,
} from "../services/mobileService";
import { isOnChartScreen } from "../services/summaryReportsService";
import { DiseaseGroupContext } from "../contexts/diseaseGroupContext";
import { BgColorToTextColorMapFunc } from "../CodesToColorMap";

export const Dashboard = () => {
	const isMobile = isXamarin();
	const heightClass = isMobile ? "h-screen" : "h-full";

	const isMounted = useRef(false);

	const location = useLocation();

	const [isInDemoMode, setIsInDemoMode] = useState(false);
	const [isRegistrarUser, setIsRegistrarUser] = useState(false);
	useEffect(() => {
		setIsInDemoMode(isDemoUser());
		setIsRegistrarUser(isRegistrar());

		if (isMobile) {
			document.body.classList.add("h-screen");
		}
	}, []);

	const [currentHelpCode, setCurrentHelpCode] = useState("");

	const [isLoading, setIsLoading] = useState(false);

	const [userPid, setUserPid] = useState(getUserPid());
	const [userSettings, setUserSettings] = useState(getUserSettings());
	const [patientName] = useState(`${userPid?.firstName} ${userPid?.surname}`);

	useEffect(() => {
		loadUserPid()
			.then((response) => {
				if (response.success === true) {
					const data = getUserPid();
					setUserPid(data);
				}
			})
			.catch(() => {});
		loadUserSettings()
			.then((response) => {
				if (response.success === true) {
					const data = getUserSettings();
					setUserSettings(data);
				}
			})
			.catch(() => {});
	}, []);

	const [isInLandscape, setIsInLandscape] = useState(false);

	const handleLandscapeChange = (value) => {
		if (isMounted.current === true) {
			setIsInLandscape(value && isOnChartScreen());
		}
	};

	useEffect(() => {
		isMounted.current = true;

		subscribeToLandscapeChange("Dashboard", handleLandscapeChange);

		isLandscape().then(handleLandscapeChange);

		return () => {
			isMounted.current = false;
			unsubscribeFromLandscapeChange("Dashboard");
		};
	}, []);

	const [setupDataItems, setSetupDataItems] = useState([]);
	useEffect(() => {
		const items = [
			{
				text: "Intro/About/Contact",
				path: "/setupdata/introduction",
				icon: "FaInfoCircle",
				isComplete: userSettings.introduction.complete,
				// diseaseGroups: ["lcn", "meso"],
			},
			{
				text: "Your Control",
				path: "/setupdata/yourcontrol",
				icon: "FaThumbsUp",
				isComplete: userSettings.yourControl.complete,
				// diseaseGroups: ["lcn", "meso"],
			},
			{
				text: "Consent",
				path: "/setupdata/consent",
				icon: "FaCheckCircle",
				isComplete: userSettings.consent.complete,
				// diseaseGroups: ["lcn", "meso"],
			},
			{
				text: "Your Details",
				path: "/setupdata/yourdetails",
				icon: "FaUserAlt",
				isComplete: userSettings.yourDetails.complete,
				// diseaseGroups: ["lcn", "meso"],
			},
			{
				text: "Long Term Conditions",
				path: "/setupdata/longtermconditions",
				icon: "BiPulse",
				isComplete: userSettings.longTermConditions.complete,
				// diseaseGroups: ["lcn", "meso"],
			},
			{
				text: "Smoking History",
				path: "/setupdata/smokinghistory",
				icon: "FaSmokingBan",
				isComplete: userSettings.smokingHistory.complete,
				// diseaseGroups: ["lcn", "meso"],
			},
			{
				text: "Asbestos History",
				path: "/setupdata/asbestoshistory",
				icon: "GiPoisonCloud",
				isComplete: userSettings.asbestosHistory.complete,
				// diseaseGroups: ["lcn", "meso"],
			},
			// {
			// 	text: "Initial Experience",
			// 	path: "/setupdata/initialexperience",
			// 	icon: "FaCommentMedical",
			// 	isComplete: userSettings.initialExperience.complete,
			// 	diseaseGroups: ["lcn", "meso"],
			// },
			{
				text: "Benefit Status",
				path: "/setupdata/benefitstatus",
				icon: "BsFillAwardFill",
				isComplete: userSettings.benefitStatus.complete,
				// diseaseGroups: ["lcn", "meso"],
			},
			{
				text: "Compensation",
				path: "/setupdata/compensation",
				icon: "BiMoney",
				isComplete: userSettings.compensation.complete,
				diseaseGroups: ["meso", "mesoperitoneal"],
			},
		];

		const diseaseGroup = getDiseaseGroup();
		const filtered = items.filter((e) => {
			if (e.diseaseGroups) {
				return e.diseaseGroups.some((w) => w === diseaseGroup);
			}

			return true;
		});

		setSetupDataItems(filtered);
	}, [userSettings]);

	// const settingsPercentage = () => {
	// 	const allSettings = setupDataItems.length;

	// 	const complete = setupDataItems.filter((e) => e.isComplete === true).length;

	// 	const raw = (complete / allSettings) * 100;

	// 	return Math.round(raw);
	// };

	const sectionsComplete = () => {
		const allSettings = setupDataItems.length;
		const suffix = allSettings === 1 ? "" : "s";

		const complete = setupDataItems.filter((e) => e.isComplete === true).length;

		return `${complete} of ${allSettings} section${suffix} complete`;
	};

	const [isCostsEnabled, setIsCostsEnabled] = useState();
	useEffect(() => {
		if (userSettings.yourControl.keepExpensesDiary === true) {
			setIsCostsEnabled(true);
		} else {
			setIsCostsEnabled(false);
		}
	}, [userSettings]);

	const [links, setLinks] = useState([]);
	const [linksHasErrored, setLinksHasErrored] = useState(false);
	useEffect(() => {
		if (location.pathname.includes("links")) {
			loadLinks();
		}
	}, [location]);

	const [locationPathname, setLocationPathname] = useState(location.pathname);
	useEffect(() => {
		if (location.pathname !== locationPathname) {
			const actions = location.pathname.endsWith("regularreports")
				? ["Due by"]
				: [];
			setTodoActions(actions);
			setLocationPathname(location.pathname);
		}
	}, [location.pathname, locationPathname]);

	const loadLinks = () => {
		setIsLoading(true);
		setLinksHasErrored(false);
		getUsefulLinks()
			.then((result) => {
				if (result.success) {
					setLinks(result.data);
				} else {
					console.error("Could not load useful links: ", result.err);
					setLinksHasErrored(true);
				}
				setIsLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLinksHasErrored(true);
				setIsLoading(false);
			});
	};

	const [isShowingHelp, setIsShowingHelp] = useState(false);

	const [isShowingSetup, setIsShowingSetup] = useState(false);

	useEffect(() => {
		setIsShowingSetup(location.pathname.includes("setupdata"));
	}, [location]);

	const renderLinks = () => {
		if (linksHasErrored) {
			return (
				<p className={"text-center text-black p-3"}>
					<>
						<p className="my-3 xs:my-10 text-center mx-2">
							It has not been possible to access our server.
						</p>
						<p className="my-3 xs:my-10 text-center mx-2">
							This is most likely because the connection to the internet was
							interrupted.
						</p>
						<p className="my-3 xs:my-10 text-center mx-2">
							If this problem persists, please contact our helpdesk, quoting
							reference '#HP001'.
						</p>
					</>
				</p>
			);
		} else if (!links || links.length === 0) {
			return <></>;
		}

		return (
			<div className="">
				<Links links={links} />
			</div>
		);
	};

	const [costsFilter, setCostsFilter] = useState("thisMonth");
	const [totalCosts, setTotalCosts] = useState();
	const [costsMonth, setCostsMonth] = useState();

	const [isShowingCostPopup, setIsShowingCostPopup] = useState(false);
	const [shouldUpdateCosts, setShouldUpdateCosts] = useState();
	const [currentCostItem, setCurrentCostItem] = useState();
	useEffect(() => {
		handleCostsFilterChange("thisMonth");
	}, [location]);

	const handleCostsFilterChange = (value) => {
		setCostsFilter(value);
		setShouldUpdateCosts(true);
	};

	const handleShowCostPopup = (costItem) => {
		setCurrentCostItem(costItem);
		setIsShowingCostPopup(true);
	};

	const handleOnCostsOffClick = () => {
		setCurrentHelpCode("CostSwitchedOff");
		setIsShowingHelp(true);
	};

	const handleHelpClicked = () => {
		let path;
		if (location.pathname.includes("summary")) {
			path = "/dashboard/summary";
		} else {
			path = location.pathname;
		}
		setCurrentHelpCode(PathsToHelpCodesMap[path] ?? "");
		setIsShowingHelp(true);
	};

	const [todoActions, setTodoActions] = useState([]);

	const { theme } = useContext(DiseaseGroupContext);
	const themePrimary = theme.primary();

	return (
		<>
			{isLoading ? <LoadingSpinner /> : null}

			<div className={`flex flex-col w-full ${heightClass}`}>
				{/* TODO FIX - userService/systemService is returning a string */}

				{/* Header */}
				<div
					className={`${
						isInLandscape ? "hidden" : ""
					} bg-white ${BgColorToTextColorMapFunc(themePrimary, "text")} z-10`}
				>
					<div className={"max-w-2xl mx-auto"}>
						<Switch>
							<Route path="/dashboard/todo">
								<DashboardHeader
									subtitle={patientName}
									iconName="MdHome"
									title="Your To-dos"
									onHelpClick={handleHelpClicked}
									demoMode={isInDemoMode}
								/>
								<NavBarTodos />
								<Route path="/dashboard/todo/nursereports">
									<div
										className={
											"px-3 xs:px-4 pt-3 sm:pt-4 mt-2 top-shadow rounded-t-xxl bg-white pb-3 sm:pb-4"
										}
									>
										<NavBarRegReports
											onSelectActions={setTodoActions}
											showNurseToDo={isInDemoMode && isRegistrarUser}
											tab={"nurseToDo"}
										/>
									</div>
								</Route>
								<Route path="/dashboard/todo/regularreports">
									<div
										className={
											"px-3 xs:px-4 pt-3 sm:pt-4 mt-2 top-shadow rounded-t-xxl bg-white pb-3 sm:pb-4"
										}
									>
										<NavBarRegReports
											onSelectActions={setTodoActions}
											showNurseToDo={isInDemoMode && isRegistrarUser}
											tab={"due"}
										/>
									</div>
								</Route>
								<Route path="/dashboard/todo/links">
									<div
										className={
											"px-3 xs:px-4 pt-3 sm:pt-4 mt-2 top-shadow rounded-t-xxl bg-white pb-3 sm:pb-4"
										}
									></div>
								</Route>
							</Route>
							<Route path="/dashboard/summary">
								<DashboardHeader
									subtitle={patientName}
									iconName="FaChartLine"
									title="Your Summary"
									onHelpClick={handleHelpClicked}
									demoMode={isInDemoMode}
								/>
								<NavBarYourSummary />
							</Route>
							<Route path="/dashboard/costs">
								<DashboardHeader
									subtitle={patientName}
									iconName="FaPoundSign"
									title="Your Costs"
									onHelpClick={handleHelpClicked}
									demoMode={isInDemoMode}
								/>
							</Route>
							<Redirect path="/dashboard" to="/dashboard/todo/regularreports" />
						</Switch>
					</div>
				</div>

				{/* Main Content */}
				<div
					className={"flex-grow overflow-y-auto relative bg-white h-[100vh]"}
				>
					<Switch>
						<Route path="/dashboard/todo/setupdata">
							<TransitionSlideUp
								show={isShowingSetup}
								enterTime={TransitionConfig.slideYTransition.enterDuration}
								leaveTime={TransitionConfig.slideYTransition.leaveDuration}
								className=" pt-4 bg-white h-full"
							>
								<div
									className={
										"top-shadow pb-3 pt-0 bg-white rounded-t-xxl h-[100vh] max-w-panel mx-auto"
									}
								>
									<p
										className={`h-full p-5 pl-6 text-md font-bold ${themePrimary.text()}`}
									>
										My Setup - {sectionsComplete()}
									</p>
									<ListPanel items={setupDataItems} />
								</div>
							</TransitionSlideUp>
						</Route>

						<Route path="/dashboard/todo/nursereports">
							<div className="p-3 xs:px-4 pt-1 lg:mt-5 xl:mt-8">
								<TodoList
									actions={todoActions}
									showNurseToDo={isInDemoMode && isRegistrarUser}
								/>
							</div>
						</Route>
						<Route path="/dashboard/todo/regularreports">
							<div className="p-3 xs:px-4 pt-1 lg:mt-5 xl:mt-8">
								<TodoList
									actions={todoActions}
									showNurseToDo={isInDemoMode && isRegistrarUser}
								/>
							</div>
						</Route>
						<Route path="/dashboard/todo/links">
							<div className="px-3 xs:px-4 pt-0 lg:mt-2 xl:mt-2">
								{renderLinks()}
							</div>
						</Route>

						<Route path="/dashboard/summary">
							<div className="absolute inset-0">
								<YourSummary />
							</div>
						</Route>

						<Route path="/dashboard/costs">
							<div
								className={
									"top-shadow pb-3 pt-1 bg-white rounded-t-xxl h-[100vh] max-w-panel mx-auto mt-6 p-3 xs:px-4"
								}
							>
								<NavBarYourCosts
									costsFilter={costsFilter}
									onCostsFilter={handleCostsFilterChange}
									onAddCost={handleShowCostPopup}
									totalCosts={totalCosts}
									costsMonth={costsMonth}
								/>
								<YourCosts
									filter={costsFilter}
									onTotalCostsChange={setTotalCosts}
									onMonthChange={setCostsMonth}
									shouldUpdate={shouldUpdateCosts}
									onUpdateFinish={() => setShouldUpdateCosts(false)}
									onShowCost={handleShowCostPopup}
								/>
							</div>
						</Route>

						<Redirect
							path="/dashboard/todo/videohelp"
							to="/dashboard/todo/videohelp/new"
						/>

						<Redirect
							path="/dashboard/todo/"
							to="/dashboard/todo/regularreports"
						/>
					</Switch>
				</div>

				{/* Footer */}
				<div
					className={` ${
						isInLandscape ? "hidden" : ""
					} z-10 footerShadow bg-white pt-3 p-safeAreaBottom `}
				>
					<NavBarMain
						isCostsEnabled={isCostsEnabled}
						onCostsOffClick={handleOnCostsOffClick}
					/>
				</div>
			</div>

			<HelpPopup
				show={isShowingHelp}
				onClose={() => setIsShowingHelp(false)}
				currentTheme={themePrimary}
				helpCode={currentHelpCode}
			/>

			<CostPopup
				show={isShowingCostPopup}
				onClose={() => setIsShowingCostPopup(false)}
				onCostSave={() => setShouldUpdateCosts(true)}
				costItem={currentCostItem}
				newCostMonth={costsFilter}
			/>
		</>
	);
};
